/**
 * Pure, side-effect-free evaluation logic for the Grader playground page
 * (bd deepcontext-hhnxm.5).
 *
 *  - `gradeFormat` mirrors the backend deterministic CODE grader
 *    (backend/src/grader/codeGrader.ts) per-URL format checks so the page can
 *    show format pass/fail without a dedicated HTTP endpoint. There is no
 *    /api/grader/code-grade route; the checks are pure on the run verdicts, so
 *    we replicate them client-side. Kept in lockstep with that module.
 *  - `aggregateRun` folds per-URL code + judge results into the run-level
 *    aggregates the page shows SEPARATELY: format % (code) and quality avg
 *    (judge), never blended.
 *
 * Everything here is a pure function so it is unit-testable without React,
 * Auth, or the network.
 */

import type { GraderVerdict, GraderJudgedUrl } from '../../services/api';

// Single-email gate shared by the page, its nav entry, and the backend
// /api/grader surface. Kept in this pure (axios-free) module so it is importable
// from tests and from AppLayout without dragging in the API client.
export const GRADER_ALLOWED_EMAIL = 'marko.dupor@eskimi.com';

export function isGraderAllowedEmail(email: string | undefined | null): boolean {
    return (email || '').toLowerCase() === GRADER_ALLOWED_EMAIL;
}

export type FormatFailureReason =
    | 'missing_verdict'
    | 'duplicate_verdict'
    | 'unknown_url'
    | 'missing_match_field'
    | 'invalid_match_type'
    | 'missing_confidence'
    | 'invalid_confidence_type'
    | 'confidence_out_of_range'
    | 'missing_reasoning'
    | 'invalid_reasoning_type'
    | 'empty_reasoning';

export interface FormatUrlResult {
    url: string;
    pass: boolean;
    failures: FormatFailureReason[];
}

export interface FormatRunResult {
    perUrl: FormatUrlResult[];
    passedCount: number;
    totalCount: number;
    /** Percent of input URLs passing format, 0..100, rounded to 2 decimals. */
    formatScore: number;
}

function normalizeUrl(url: unknown): string | null {
    if (typeof url !== 'string') return null;
    const trimmed = url.trim();
    return trimmed === '' ? null : trimmed;
}

/**
 * Deterministic per-URL format grading, mirroring backend gradeCodeFormat:
 *   - exactly one verdict per input URL (no missing, no duplicate);
 *   - isBrandSafe is a boolean;
 *   - confidence is a number in [0, 1];
 *   - reasoning is a non-empty string.
 */
export function gradeFormat(inputUrls: string[], verdicts: GraderVerdict[]): FormatRunResult {
    const inputOrder: string[] = [];
    const inputSet = new Set<string>();
    for (const raw of inputUrls) {
        const url = normalizeUrl(raw);
        if (url === null || inputSet.has(url)) continue;
        inputSet.add(url);
        inputOrder.push(url);
    }

    const verdictsByUrl = new Map<string, GraderVerdict[]>();
    for (const verdict of verdicts) {
        const url = normalizeUrl(verdict?.url);
        if (url === null || !inputSet.has(url)) continue;
        const bucket = verdictsByUrl.get(url);
        if (bucket) bucket.push(verdict);
        else verdictsByUrl.set(url, [verdict]);
    }

    const perUrl: FormatUrlResult[] = inputOrder.map((url) => {
        const bucket = verdictsByUrl.get(url) || [];
        const failures: FormatFailureReason[] = [];

        if (bucket.length === 0) {
            failures.push('missing_verdict');
            return { url, pass: false, failures };
        }
        if (bucket.length > 1) {
            failures.push('duplicate_verdict');
        }

        const verdict = bucket[0];

        if (!Object.prototype.hasOwnProperty.call(verdict, 'isBrandSafe')) {
            failures.push('missing_match_field');
        } else if (typeof verdict.isBrandSafe !== 'boolean') {
            failures.push('invalid_match_type');
        }

        if (!Object.prototype.hasOwnProperty.call(verdict, 'confidence')) {
            failures.push('missing_confidence');
        } else {
            const conf = verdict.confidence;
            if (typeof conf !== 'number' || Number.isNaN(conf)) {
                failures.push('invalid_confidence_type');
            } else if (conf < 0 || conf > 1) {
                failures.push('confidence_out_of_range');
            }
        }

        if (!Object.prototype.hasOwnProperty.call(verdict, 'reasoning')) {
            failures.push('missing_reasoning');
        } else if (typeof verdict.reasoning !== 'string') {
            failures.push('invalid_reasoning_type');
        } else if (verdict.reasoning.trim() === '') {
            failures.push('empty_reasoning');
        }

        return { url, pass: failures.length === 0, failures };
    });

    const totalCount = perUrl.length;
    const passedCount = perUrl.filter((r) => r.pass).length;
    const formatScore =
        totalCount === 0 ? 0 : Math.round((passedCount / totalCount) * 10000) / 100;

    return { perUrl, passedCount, totalCount, formatScore };
}

/** One fully-merged per-URL row for the results table. */
export interface GraderResultRow {
    url: string;
    verdict: GraderVerdict | null;
    /** Deterministic format pass/fail for this URL. */
    formatPass: boolean;
    formatFailures: FormatFailureReason[];
    /** Judge quality score 0-10 (undefined until judged). */
    qualityScore?: number;
    judgeReasoning?: string;
    judgeStrengths?: string[];
    judgeWeaknesses?: string[];
}

/** Run-level aggregates, kept SEPARATE: format is code, quality is judge. */
export interface GraderRunAggregates {
    /** Percent of URLs passing deterministic format checks, 0..100. */
    formatScore: number;
    formatPassedCount: number;
    formatTotalCount: number;
    /** Mean judge quality 0-10, undefined when nothing judged yet. */
    qualityAvg: number | undefined;
    qualityJudgedCount: number;
}

/**
 * Merge run verdicts + (optional) judge results into per-URL rows plus the two
 * run-level aggregates the page shows separately. `inputUrls` drives row order
 * and identity so a missing verdict still surfaces as a failing row.
 */
export function buildResultRows(
    inputUrls: string[],
    verdicts: GraderVerdict[],
    judged: GraderJudgedUrl[] = [],
): { rows: GraderResultRow[]; aggregates: GraderRunAggregates } {
    const format = gradeFormat(inputUrls, verdicts);
    const formatByUrl = new Map(format.perUrl.map((r) => [r.url, r]));
    const verdictByUrl = new Map<string, GraderVerdict>();
    for (const v of verdicts) {
        const url = normalizeUrl(v?.url);
        if (url !== null && !verdictByUrl.has(url)) verdictByUrl.set(url, v);
    }
    const judgedByUrl = new Map<string, GraderJudgedUrl>();
    for (const j of judged) {
        const url = normalizeUrl(j?.url);
        if (url !== null && !judgedByUrl.has(url)) judgedByUrl.set(url, j);
    }

    const seen = new Set<string>();
    const orderedUrls: string[] = [];
    for (const raw of inputUrls) {
        const url = normalizeUrl(raw);
        if (url === null || seen.has(url)) continue;
        seen.add(url);
        orderedUrls.push(url);
    }

    const rows: GraderResultRow[] = orderedUrls.map((url) => {
        const fmt = formatByUrl.get(url);
        const j = judgedByUrl.get(url);
        return {
            url,
            verdict: verdictByUrl.get(url) ?? null,
            formatPass: fmt ? fmt.pass : false,
            formatFailures: fmt ? fmt.failures : ['missing_verdict'],
            qualityScore: j ? j.score : undefined,
            judgeReasoning: j ? j.reasoning : undefined,
            judgeStrengths: j ? j.strengths : undefined,
            judgeWeaknesses: j ? j.weaknesses : undefined,
        };
    });

    const qualityScores = rows
        .map((r) => r.qualityScore)
        .filter((s): s is number => typeof s === 'number' && Number.isFinite(s));
    const qualityAvg =
        qualityScores.length === 0
            ? undefined
            : Math.round((qualityScores.reduce((a, b) => a + b, 0) / qualityScores.length) * 100) / 100;

    return {
        rows,
        aggregates: {
            formatScore: format.formatScore,
            formatPassedCount: format.passedCount,
            formatTotalCount: format.totalCount,
            qualityAvg,
            qualityJudgedCount: qualityScores.length,
        },
    };
}

/** A completed run captured for session-local comparison (v1 → v2 → v3). */
export interface GraderRunRecord {
    /** 1-based version within this session. */
    version: number;
    label: string;
    promptId: string;
    sampleSize: number;
    prompt: string;
    rows: GraderResultRow[];
    aggregates: GraderRunAggregates;
    judged: boolean;
    createdAt: number;
}

/** Append a finished run to the session history, assigning the next version. */
export function appendRunRecord(
    history: GraderRunRecord[],
    record: Omit<GraderRunRecord, 'version' | 'label' | 'createdAt'>,
): GraderRunRecord[] {
    const version = history.length + 1;
    const next: GraderRunRecord = {
        ...record,
        version,
        label: `v${version}`,
        createdAt: Date.now(),
    };
    return [...history, next];
}
